import { Stack } from "@mui/material";
import { useLocation, useMatch } from "react-router";
import { currencyFormatter } from "@platform/fe-utils";

import { ROUTE_NAMES } from "../../../../constants";
import {
  useScenariosSummaryQuery,
  useBrokerSummaryWithCarrierQuery,
} from "../../../../hooks/useBbe";

import {
  HeaderExtraBackgroundFormat,
  HeaderExtraBrokerSettingsIcon,
  HeaderExtraFormat,
} from "./HeaderExtraFormat";

type ScenarioModelingParentHeaderExtraProps = {
  snapshotTotalEstimatedBonus: string;
  goal: string;
};

const ScenarioModelingParentHeaderExtra: React.FC<
  ScenarioModelingParentHeaderExtraProps
> = ({ snapshotTotalEstimatedBonus, goal }) => (
  <Stack direction="row" spacing={3} px={2}>
    <HeaderExtraFormat
      title="Snapshot Total Estimated Bonus"
      description={snapshotTotalEstimatedBonus}
    />

    <HeaderExtraFormat title="Goal" description={goal} />
  </Stack>
);

type ScenarioModelingChildrenHeaderExtraProps = {
  baselineBonus: string;
  scenarioBonus: string;
};

const ScenarioModelingChildrenHeaderExtra: React.FC<
  ScenarioModelingChildrenHeaderExtraProps
> = ({ baselineBonus, scenarioBonus }) => (
  <Stack direction="row" spacing={3} px={2}>
    <HeaderExtraFormat
      title="Baseline Estimated Bonus"
      description={baselineBonus}
    />
    <HeaderExtraFormat
      title="Scenario Estimated Bonus"
      description={scenarioBonus}
    />
  </Stack>
);

type ScenarioModelingHeaderExtraProps = {
  brokerId: string;
  selectedModelId: string;
  navigateToBrokerSettingPage: () => void;
};

export const ScenarioModelingHeaderExtra: React.FC<
  ScenarioModelingHeaderExtraProps
> = ({ brokerId, selectedModelId, navigateToBrokerSettingPage }) => {
  const location = useLocation();

  const { data: scenarioData } = useScenariosSummaryQuery(selectedModelId);
  const { data: carrierSummaries } = useBrokerSummaryWithCarrierQuery(brokerId);

  const baselineScenario = scenarioData?.scenarios.find((s) => s.isBaseline);
  const snapshotTotalEstimatedBonus = currencyFormatter(
    baselineScenario?.bobBonus,
  );

  const scenarioMatch = useMatch(
    `/${ROUTE_NAMES.BBE}/:brokerId/${ROUTE_NAMES.BBE_SCENARIO_MODELING}/:modelId/:scenarioId`,
  );
  const currentScenarioId = scenarioMatch?.params.scenarioId;
  const currentScenario = scenarioData?.scenarios.find(
    (s) => s.id === currentScenarioId,
  );

  const baselineBonus = currencyFormatter(baselineScenario?.bobBonus);
  const scenarioBonus = currencyFormatter(currentScenario?.bobBonus);

  const goalValue = carrierSummaries?.reduce((sum, carrier) => {
    const currentSummary =
      carrier.brokerSummaries.find((s) => s.isCurrent) ??
      carrier.brokerSummaries[0];
    return sum + (currentSummary?.bonusGoal ?? 0);
  }, 0);
  const goal = currencyFormatter(goalValue);

  const isScenarioModelingSubRoute = !!(
    location.pathname.split(`/${ROUTE_NAMES.BBE_SCENARIO_MODELING}`)[1]
      ?.length ?? 0
  );
  return (
    <Stack direction="row" spacing={1}>
      <HeaderExtraBackgroundFormat>
        {!isScenarioModelingSubRoute && (
          <ScenarioModelingParentHeaderExtra
            snapshotTotalEstimatedBonus={snapshotTotalEstimatedBonus}
            goal={goal}
          />
        )}
        {isScenarioModelingSubRoute && (
          <ScenarioModelingChildrenHeaderExtra
            baselineBonus={baselineBonus}
            scenarioBonus={scenarioBonus}
          />
        )}
      </HeaderExtraBackgroundFormat>

      {!isScenarioModelingSubRoute && (
        <HeaderExtraBackgroundFormat>
          <HeaderExtraBrokerSettingsIcon
            navigateToBrokerSettingPage={navigateToBrokerSettingPage}
          />
        </HeaderExtraBackgroundFormat>
      )}
    </Stack>
  );
};
